// src/components/DashboardFamily.js
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  doc
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebase';
import { generateInvitationCode } from '../utils/generateInvitationCode';
import React from 'react';

/**
 * Attende che Firebase stabilisca lo stato di autenticazione.
 * Restituisce una Promise che risolve con l'utente (o null) quando lo stato è noto.
 */
export const waitForAuth = () =>
  new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });

/**
 * Recupera tutte le famiglie a cui l'utente (userId) appartiene,
 * e imposta i dati nello stato tramite setFamilies.
 */
export const fetchFamilies = async (userId, setFamilies) => {
  try {
    // Attende che l'utente sia definito (se necessario)
    const user = await waitForAuth();
    if (!user || user.uid !== userId) {
      console.error('Utente non autenticato o UID non corrispondente.');
      return;
    }
    const familiesQuery = query(
      collection(db, 'families'),
      where('members', 'array-contains', userId)
    );
    const querySnapshot = await getDocs(familiesQuery);
    const familiesData = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setFamilies(familiesData);
  } catch (error) {
    console.error('Errore nel recupero delle famiglie:', error);
  }
};

/**
 * Crea una nuova famiglia con un codice di invito e aggiunge l'utente corrente come primo membro.
 */
export const handleCreateFamily = async (setFamilies, setUserFamily) => {
  try {
    const currentUser = await waitForAuth();
    if (!currentUser) {
      console.error('Utente non autenticato.');
      return;
    }
    // Genera il codice di invito in modo univoco
    const invitationCode = generateInvitationCode();
    // Prompt per il nome della famiglia
    const familyName = prompt('Inserisci il nome della tua famiglia:');
    if (!familyName) {
      alert('Il nome della famiglia è richiesto.');
      return;
    }
    // Crea il documento della famiglia nella collezione "families"
    const familyRef = await addDoc(collection(db, 'families'), {
      name: familyName,
      members: [currentUser.uid],
      invitationCode,
      createdAt: new Date(),
    });
    // Aggiorna lo stato delle famiglie
    await fetchFamilies(currentUser.uid, setFamilies);
    // Imposta lo stato userFamily come la nuova famiglia creata
    setUserFamily({ id: familyRef.id, name: familyName });
    alert(`Famiglia creata con successo! Codice di invito: ${invitationCode}`);
  } catch (error) {
    console.error('Errore nella creazione della famiglia:', error);
    alert('Errore nella creazione della famiglia.');
  }
};

/**
 * Unisce l'utente corrente a una famiglia, cercandola tramite il codice di invito.
 * Aggiorna anche il documento utente (collezione "users") impostando il campo familyId.
 */
export const handleJoinFamily = async (inviteCode, setFamilies, setUserFamily) => {
  try {
    const currentUser = await waitForAuth();
    if (!currentUser) {
      console.error('Utente non autenticato.');
      return;
    }
    // Cerca la famiglia con il codice di invito fornito
    const familyQuery = query(
      collection(db, 'families'),
      where('invitationCode', '==', inviteCode)
    );
    const querySnapshot = await getDocs(familyQuery);
    if (querySnapshot.empty) {
      alert('Codice di invito non valido.');
      return;
    }
    const familyDoc = querySnapshot.docs[0];
    const familyData = familyDoc.data();
    const familyId = familyDoc.id;
    // Verifica se l'utente è già in una famiglia
    const userFamilyQuery = query(
      collection(db, 'families'),
      where('members', 'array-contains', currentUser.uid)
    );
    const userFamilySnapshot = await getDocs(userFamilyQuery);
    if (!userFamilySnapshot.empty) {
      alert('Sei già in una famiglia. Devi prima uscire per unirti a una nuova.');
      return;
    }
    // Aggiungi l'utente alla famiglia
    const familyRef = doc(db, 'families', familyId);
    await updateDoc(familyRef, {
      members: arrayUnion(currentUser.uid),
    });
    // Aggiorna il documento utente con il campo familyId
    const userRef = doc(db, 'users', currentUser.uid);
    await updateDoc(userRef, {
      familyId: familyId,
    });
    // Ricarica le famiglie e imposta la famiglia corrente
    await fetchFamilies(currentUser.uid, setFamilies);
    setUserFamily({ id: familyId, name: familyData.name });
    alert(`Ti sei unito alla famiglia "${familyData.name}" con successo!`);
  } catch (error) {
    console.error('Errore nell\'unirsi alla famiglia:', error);
    alert('Errore nell\'unirsi alla famiglia.');
  }
};

/**
 * Rimuove l'utente corrente dalla famiglia (identificata da userFamilyId)
 * e aggiorna il documento utente impostando familyId a null.
 */
export const handleLeaveFamily = async (userFamilyId, setUserFamily, setFamilies) => {
  try {
    const currentUser = await waitForAuth();
    if (!currentUser) {
      console.error('Utente non autenticato.');
      return;
    }
    const familyRef = doc(db, 'families', userFamilyId);
    await updateDoc(familyRef, {
      members: arrayRemove(currentUser.uid),
    });
    const userRef = doc(db, 'users', currentUser.uid);
    await updateDoc(userRef, {
      familyId: null,
    });
    setUserFamily(null);
    setFamilies([]);
    alert('Hai lasciato la famiglia. Ora sei nel tuo spazio personale.');
  } catch (error) {
    console.error('Errore nell\'uscire dalla famiglia:', error);
    alert('Errore nell\'uscire dalla famiglia.');
  }
};

/**
 * Recupera i dati della famiglia a cui l'utente appartiene e li imposta tramite setUserFamily.
 */
export const fetchUserFamily = async (setUserFamily) => {
  try {
    const currentUser = await waitForAuth();
    if (!currentUser) {
      console.error('Utente non autenticato.');
      setUserFamily(null);
      return;
    }
    const userId = currentUser.uid;
    const familiesQuery = query(
      collection(db, 'families'),
      where('members', 'array-contains', userId)
    );
    const familySnapshot = await getDocs(familiesQuery);
    if (!familySnapshot.empty) {
      const familyDoc = familySnapshot.docs[0];
      setUserFamily({ id: familyDoc.id, ...familyDoc.data() });
      console.log('Famiglia trovata:', { id: familyDoc.id, ...familyDoc.data() });
    } else {
      console.log('Nessuna famiglia trovata per l\'utente.');
      setUserFamily(null);
    }
  } catch (error) {
    console.error('Errore durante il recupero della famiglia:', error);
    setUserFamily(null);
  }
};
